import {
	AfterViewInit,
	Component,
	Input,
	OnInit
}                                     from '@angular/core';
import {
	AbstractControl,
	UntypedFormGroup
}                                     from '@angular/forms';
import { ENTITY_TYPE }                from '@app/core/interfaces/entity.interface';
import {
	FormDataTreatmentEnum,
	IFormBaseData,
	IFormData
}                                     from '@app/core/interfaces/form.interface';
import {
	ArrayUtils,
	ObjectUtils
}                                     from '@app/core/utils/helpers';
import { Organization }               from '@app/modules/organization/models/organization.model';
import { OrganizationService }        from '@app/modules/organization/services/organization.service';
import { RequestCompleteEntityState } from '@core/interfaces/api.interface';
import { AbstractModalBase }          from '@core/modal-base/abstractModalBase';
import {
	IModalBaseEvent,
	ModalBaseEventTypeEnum,
	ModalIdentifierEnum
}                                     from '@core/modals/modal.model';
import {
	SELECT_VERSION_MODAL_CONFIG,
	SelectVersionModalActionsEnum,
	SelectVersionModalComparisonResultEnum,
	SelectVersionModalVersionTypeEnum
}                                     from '@core/modals/select-version-modal/select-version-modal.config';
import { AuthorizationService }       from '@core/services/authorization/authorization.service';
import { ValueTypeEnum }              from '@models/common.model';
import { NgbActiveModal }             from '@ng-bootstrap/ng-bootstrap';
import { CountryI18n }                from '@shared/components/country/models/country-country.model';
import {
	ITableConfig,
	TableResponsiveBreakpointEnum
}                                     from '@shared/components/table/table.model';

@Component( {
	selector: 'app-select-version-modal',
	templateUrl: './select-version-modal.component.html',
	styleUrls: [ './select-version-modal.component.scss' ]
} )
export class SelectVersionModalComponent extends AbstractModalBase implements AfterViewInit, OnInit {

	private readonly _modalConfig = SELECT_VERSION_MODAL_CONFIG;
	private _missingKeyInFormData: keyof IFormBaseData = null;
	private _modalIdentifier: ModalIdentifierEnum;

	public bodyTableConfig: ITableConfig;
	public readonly comparisonResult = SelectVersionModalComparisonResultEnum;
	public hasOverwrites = false;
	public headerTableConfig: ITableConfig;
	public hoveredVersion: string;
	public list: IFormData[];
	public resultHeaderTableConfig: ITableConfig;
	public resultBodyTableConfig: ITableConfig;
	public showDiff: boolean = this._modalConfig.showDiffInit;
	public showOverwrite: boolean = this._modalConfig.showOverwriteInit;
	public showResult: boolean = this._modalConfig.showResultInit;
	public selectedVersion: string;
	public searchRevert = true;
	public readonly versionType = SelectVersionModalVersionTypeEnum;
	public withClipboard: boolean = this._modalConfig.withClipboard;

	get modalIdentifier(): ModalIdentifierEnum {
		return this._modalIdentifier;
	}

	@Input() set modalIdentifier( modalIdentifier: ModalIdentifierEnum ) {
		this._modalIdentifier = modalIdentifier;
	}

	@Input() dbVersion: ENTITY_TYPE;
	@Input() form: UntypedFormGroup;
	@Input() formConfigData: IFormBaseData;
	@Input() localVersion: ENTITY_TYPE;


	constructor(
		private readonly _authorizationService: AuthorizationService,
		public activeModal: NgbActiveModal,
		public readonly orgService: OrganizationService
	) {
		super();
		this.translationPath += `.${ this._modalConfig.translationPath }`;
	}


	// CLIPBOARD ###########################################################
	private generateClipboardString(): string {
		let dbContent = '--------------------------------------------------------------------------------------\r\n';
		let localContent = '--------------------------------------------------------------------------------------\r\n';
		let clipboardContent = '';

		dbContent += `${ this.getI18nString( true, 'clipboard.dbTitle' ) }\r\n`;
		localContent += `${ this.getI18nString( true, 'clipboard.localTitle' ) }\r\n`;

		dbContent += '--------------------------------------------------------------------------------------\r\n';
		localContent += '--------------------------------------------------------------------------------------\r\n';

		this.list.forEach( listItem => {
			const label = `${ this.getI18nString( false, listItem.i18nPath + '.label' ) }: `;
			dbContent += listItem.dbValue ? `${ label }${ listItem.dbValue }\r\n` : `${ label }\r\n`;
			localContent += listItem.localValue ? `${ label }${ listItem.localValue }\r\n` : `${ label }\r\n`;
		} );

		clipboardContent += dbContent;
		clipboardContent += '\r\n\r\n';
		clipboardContent += localContent;

		return clipboardContent;
	}

	// POPULATE LIST ########################################################

	private async treatmentsHandler( treatment: FormDataTreatmentEnum, formItem: IFormData ): Promise<void> {

		const formItemKeysToBeMapped: string[] = [ 'localValue', 'dbValue' ];

		switch ( treatment ) {

			case FormDataTreatmentEnum.BOOLEAN_TO_I18N:
				formItemKeysToBeMapped.forEach( ( key: string ) => {
					formItem[ key ] = formItem[ key ] === true
														? this.getI18nString( false, 'shared.booleanTo.state.true' )
														: this.getI18nString( false, 'shared.booleanTo.state.false' );
				} );
				break;

			case FormDataTreatmentEnum.COUNTRY_CODE_TO_I18N:
				const countries: CountryI18n = this.getI18nString( false, 'countries' ) as CountryI18n;

				formItemKeysToBeMapped.forEach( ( key: string ) => {
					const country: string = countries[ formItem[ key ] ];
					formItem[ key ] = country ?? formItem[ key ];
				} );
				break;

			case FormDataTreatmentEnum.EMPTY_PARENT_ORG_ID_TO_ROOT_LEVEL:
				formItemKeysToBeMapped.forEach( ( key: string ) => {
					formItem[ key ] = !formItem[ key ] || formItem[ key ].length === 0
														? this.getI18nString( false, `shared.forms.parentOrgId.rootLvl` )
														: formItem[ key ];
				} );
				break;

			case FormDataTreatmentEnum.DATE_TO_LOCALE_STRING:
				const lang: string = this.uiService.getLanguage( true ) || 'en'; // get language with fallback to en
				const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

				formItemKeysToBeMapped.forEach( ( key: string ) => {
					formItem[ key ] = formItem[ key ] instanceof Date ? formItem[ key ].toLocaleString( lang, options ) : formItem[ key ];
				} );
				break;

			case FormDataTreatmentEnum.ERS_DEVICE_TYPE_TO_I18N:
				this.mapFormItemValuesToI18n( formItem, formItemKeysToBeMapped, 'ers.deviceType' );
				break;

			case FormDataTreatmentEnum.LEGAL_FORM_TO_I18N:
				this.mapFormItemValuesToI18n( formItem, formItemKeysToBeMapped, 'organization.legalForm' );
				break;

			case FormDataTreatmentEnum.HAS_PERMISSION:
				if ( formItem.hide ) { return; }
				if ( !formItem.permissionData ) {
					this.logger.debug(
						'SelectVersionModalComponent:treatmentsHandler',
						`FormDataItem '${ formItem.formControlName } is marked to ask for permission, but does not provide permission data!`
					);
					return;
				}

				const hasPermissions: boolean = this._authorizationService.hasAllPermissions(
					formItem.permissionData.scopes,
					formItem.permissionData.permissionCheck
				);

				formItem.hide = !hasPermissions;
				break;

			case FormDataTreatmentEnum.HIDE_CONDITIONALLY:
				if ( formItem.hide ) { return; }
				if ( !formItem.hideConditionally ) {
					this.logger.debug(
						'SelectVersionModalComponent:treatmentsHandler',
						`FormDataItem '${ formItem.formControlName } is marked to be hidden conditionally, but does not provide a 'conditionallyHide' callback!`
					);
					return;
				}

				formItem.hide = formItem.hideConditionally( formItem, this.localVersion, this.dbVersion );
				break;

			case FormDataTreatmentEnum.ORG_ID_TO_NAME:
				const conflictingVersionsByFormItemKey = { localValue: this.localVersion, dbValue: this.dbVersion };

				await ArrayUtils.asyncForEach( formItemKeysToBeMapped, async ( key: string ) => {
					if ( ObjectUtils.deepRead( conflictingVersionsByFormItemKey[ key ], formItem.objectKeyPath ) ) {
						const orgReadResponse: RequestCompleteEntityState<Organization> = await this.orgService.read( { id: formItem[ key ] } ).toPromise();
						formItem[ key ] = orgReadResponse.entity.name;
					}
				} );
				break;

			case FormDataTreatmentEnum.SALUTATION_TO_I18N:
				this.mapFormItemValuesToI18n( formItem, formItemKeysToBeMapped, 'shared.salutation' );
				break;

			case FormDataTreatmentEnum.STATUS_TO_I18N:
				this.mapFormItemValuesToI18n( formItem, formItemKeysToBeMapped, 'shared.status' );
				break;

			default:
				this.logger.log( `no treatment for '${ treatment }' available.` );
		}
	}

	private mapFormItemValuesToI18n( formItem: IFormData, formItemValueKeys: string[], i18nPath: string ): void {
		formItemValueKeys.forEach( ( key: string ) => {
			formItem[ key ] = formItem[ key ]
												? this.getI18nString( false, `${ i18nPath }.${ formItem[ key ] }` )
												: '';
		} );
	}

	private async compareVersions( formItem: IFormData ): Promise<void> {
		formItem.localValue = ObjectUtils.deepRead( this.localVersion, formItem.objectKeyPath );
		formItem.dbValue = ObjectUtils.deepRead( this.dbVersion, formItem.objectKeyPath );

		if ( formItem.specialTreatments ) {
			await ArrayUtils.asyncForEach( Object.entries( formItem.specialTreatments ), async ( [ idx, treatment ] ) => {
				await this.treatmentsHandler( treatment, formItem );
			} );
		}

		formItem.comparisonResult = formItem.disabled
																? this.comparisonResult.WILL_OVERWRITE
																: formItem.localValue !== formItem.dbValue
																	? this.comparisonResult.IS_DIFFERENT
																	: this.comparisonResult.IS_SAME;


		formItem.overwriteValue = formItem.comparisonResult === this.comparisonResult.WILL_OVERWRITE
															? formItem.dbValue
															: formItem.localValue;
	}

	private async populateMetaDataList( controls: { [ key: string ]: AbstractControl }, formData, objectKey: string = null ): Promise<void> {
		await ArrayUtils.asyncForEach( Object.entries( controls ), async ( [ key, val ] ) => {
			const objectKeyPath = objectKey ? objectKey + '.' + key : key;

			// skip etag formControl
			if ( key === 'etag' ) { return; }

			// check for missing formControl keys in formData configuration
			if ( !formData[ key ] && !(
				val instanceof UntypedFormGroup
			) ) {
				this._missingKeyInFormData = key;
				return;
			}

			// traverse nested controls
			if ( val instanceof UntypedFormGroup ) {
				await this.populateMetaDataList( val.controls, formData, objectKeyPath );

				// populate list for diff table
			} else if ( formData[ key ].hide !== true ) {
				const formItem: IFormData = Object.assign( {
					...formData[ key ],
					formControlName: key,
					disabled: val.disabled,
					objectKeyPath
				} );

				await this.compareVersions( formItem );

				/*
				 * Check for `hide` again, because it might have been conditionally set
				 * to `true` during treatments processing in version comparison
				 */
				if ( formItem.hide !== true ) {
					this.list.push( formItem );
				}
			}
		} );

		if ( this._missingKeyInFormData ) {
			const errorMessage = `Key '${ this._missingKeyInFormData }' is missing in form data`;
			this.toaster.error( errorMessage );
			this.activeModal.close( { action: SelectVersionModalActionsEnum.ERROR, data: errorMessage } );
		}

		// TODO should be inside of ngInit (workaround: sort and overwrite check is called before population finished)
		this.list.sort( ( formItemDataA: IFormData, formItemDataB: IFormData ) => {
			if ( formItemDataA.order === formItemDataB.order ) { return 0; }
			return formItemDataA.order > formItemDataB.order ? 1 : -1;
		} );

		this.logger.debug( 'sortedList: ', this.list );

		this.hasOverwrites =
			this.list.some(
				( listItem: IFormData ) => listItem.comparisonResult === this.comparisonResult.WILL_OVERWRITE && listItem.dbValue !== listItem.localValue );
	}

	updateOptions( modelName: string ): void {
		if ( modelName === 'showResult' ) { this.showResult = !this.showResult; }
		if ( modelName === 'showDiff' ) { this.showDiff = !this.showDiff; }
		if ( modelName === 'showOverwrite' ) { this.showOverwrite = !this.showOverwrite; }
		if ( modelName === 'withClipboard' ) { this.withClipboard = !this.withClipboard; }
	}

	async ngOnInit(): Promise<void> {
		if ( this.dbVersion === null || this.localVersion === undefined || this.form === null || this.formConfigData === null ) {
			this.activeModal.close( { action: SelectVersionModalActionsEnum.ERROR, data: 'Inputs are missing' } );
		}
		this.list = [];
		this._missingKeyInFormData = null;

		await this.populateMetaDataList( this.form.controls, this.formConfigData );


		this.headerTableConfig = {
			bordered: false,
			compact: true,
			responsive: {
				isActive: true,
				breakpoint: TableResponsiveBreakpointEnum.MD
			},
			stripped: false,
			body: { isRendered: false },
			header: {
				isRendered: true,
				rowData: [
					{
						class: 'd-flex',
						cellData: [
							{ class: 'd-flex col-4 px-0', enforceCustomTemplate: true, value: null, valueType: ValueTypeEnum.VALUE, identifier: 'options' },
							{ class: 'd-flex col-4 px-0', enforceCustomTemplate: true, value: null, valueType: ValueTypeEnum.VALUE, identifier: 'local' },
							{ class: 'd-flex col-4 px-0', enforceCustomTemplate: true, value: null, valueType: ValueTypeEnum.VALUE, identifier: 'db' }
						]
					}
				]
			}
		};

		this.resultHeaderTableConfig = {
			...this.headerTableConfig,
			header: {
				...this.headerTableConfig.header,
				rowData: [
					{
						class: 'd-flex',
						cellData: [
							{ class: 'd-flex col-4 px-0', enforceCustomTemplate: true, value: null, valueType: ValueTypeEnum.VALUE, identifier: 'options' },
							{ class: 'd-flex col-8 px-0', enforceCustomTemplate: true, value: null, valueType: ValueTypeEnum.VALUE, identifier: 'result' }
						]
					}
				]
			}
		};

		this.bodyTableConfig = {
			...this.headerTableConfig,
			header: { isRendered: false },
			body: {
				isRendered: true,
				rowData: this.list?.map( ( formData: IFormData ) => {
					return {
						class: 'd-flex',
						cellData: [
							{
								class: 'd-flex col-4 p-0',
								enforceCustomTemplate: true,
								value: formData,
								valueType: ValueTypeEnum.VALUE,
								identifier: 'label'
							},
							{
								class: 'd-flex col-4 p-0 versionItemWrapper',
								enforceCustomTemplate: true,
								value: formData,
								valueType: ValueTypeEnum.VALUE,
								identifier: 'local'
							},
							{
								class: 'd-flex col-4 p-0 versionItemWrapper',
								enforceCustomTemplate: true,
								value: formData,
								valueType: ValueTypeEnum.VALUE,
								identifier: 'db'
							}
						]
					};
				} )
			}
		};

		this.resultBodyTableConfig = {
			...this.bodyTableConfig,
			body: {
				...this.bodyTableConfig.body,
				rowData: this.list?.map( ( formData: IFormData ) => {
					return {
						class: 'd-flex',
						cellData: [
							{
								class: 'd-flex col-4 p-0',
								enforceCustomTemplate: true,
								value: formData,
								valueType: ValueTypeEnum.VALUE,
								identifier: 'label'
							},
							{
								class: 'd-flex col-8 p-0 versionItemWrapper',
								enforceCustomTemplate: true,
								value: formData,
								valueType: ValueTypeEnum.VALUE,
								identifier: 'result'
							}
						]
					};
				} )
			}
		};


		// TODO see described workaround in populateMetaDataList
		// this.list.sort( ( formItemDataA, formItemDataB ) => {
		// 	if ( formItemDataA.order === formItemDataB.order ) { return 0; }
		// 	return formItemDataA.order > formItemDataB.order ? 1 : -1;
		// } );
		//
		// this.logger.debug( 'sortedList: ', this.list );
		//
		// this.hasOverwrites =
		// 	this.list.some(
		// 		( listItem ) => listItem.comparisonResult === this.comparisonResult.WILL_OVERWRITE && listItem.dbValue !== listItem.localValue );
	}

	ngAfterViewInit(): void {}

	onModalBaseEvent( modalBaseEvent: IModalBaseEvent ): void {
		switch ( modalBaseEvent?.event ) {
			case ModalBaseEventTypeEnum.CLOSE:
				this.activeModal.close(
					{
						action: ModalBaseEventTypeEnum.CLOSE
					}
				);
				break;
		}
	}

	abort(): void {
		this.logger.log( 'SelectVersionModal abort not implemented' );
	}

	confirm(): void {
		this.logger.log( 'confirm call selectedVersion: ', this.selectedVersion );
		let result;

		if ( this.selectedVersion === this.versionType.LOCAL ) {
			result = this.localVersion;

			// assign overwrites
			this.list.forEach( ( listItem ) => {
				if ( listItem.comparisonResult === this.comparisonResult.WILL_OVERWRITE ) {
					ObjectUtils.deepWrite( result, listItem.objectKeyPath, ObjectUtils.deepRead( this.dbVersion, listItem.objectKeyPath ) );
				}
			} );
		} else {
			result = this.dbVersion;
		}

		result = { ...result, etag: this.dbVersion.etag };

		if ( this.withClipboard ) {
			this.clipboardContent = this.generateClipboardString();
			this.copyToClipboard( null, true );
		}

		this.activeModal.close( { action: SelectVersionModalActionsEnum.CONFIRM, data: result } );
	}
}
